import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  ClockIcon,
  CurrencyDollarIcon,
  SparklesIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';

const Homepage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-black border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img 
                src="/horizonbg-logo.svg" 
                alt="HorizonBG" 
                className="h-8 w-auto"
              />
              <span className="text-xl font-semibold text-white">GP Ads AI</span>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/signin"
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Sign In
              </Link>
              <Link
                to="/signup"
                className="bg-gp-sungold text-black hover:bg-gp-sungold-400 px-4 py-2 rounded-md text-sm font-semibold transition-colors"
              >
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 pb-32 bg-gradient-to-b from-gray-900 via-gray-800 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Build Trust in
              <span className="text-gp-sungold"> Every Ad Dollar</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Complete AI-powered advertising platform for advocacy teams—where humans stay in control.
              Campaign management, creative studio, advanced analytics, and governance tools unified 
              across Meta, Google Ads, and TikTok. 35+ features working together to accelerate decisions 
              without removing accountability.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/signup"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-semibold rounded-md text-black bg-gp-sungold hover:bg-gp-sungold-400 transition-colors"
              >
                Start Free Trial
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
              <button className="inline-flex items-center px-6 py-3 border-2 border-gray-600 text-base font-medium rounded-md text-white bg-transparent hover:bg-gray-800 transition-colors">
                Request Demo
              </button>
            </div>
            
            {/* Hero Image Placeholder */}
            <div className="mt-16 rounded-lg shadow-2xl overflow-hidden border border-gray-700">
              <div className="bg-gradient-to-br from-gray-800 via-black to-gray-900 h-96 flex items-center justify-center">
                <p className="text-gray-400 text-lg">Dashboard Preview Coming Soon</p>
                {/* Will be replaced with AI-generated image */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Stop Juggling Tools. Start Building Trust.
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Managing campaigns across Meta, Google, and TikTok shouldn't require spreadsheets,
              email chains, and crossed fingers.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-red-600 mb-4">Without GP Ads AI</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✗</span>
                  <span className="text-gray-600">20+ hours/week juggling Meta Ads Manager, Google Ads, TikTok Ads</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✗</span>
                  <span className="text-gray-600">Scattered data in spreadsheets, manual cross-platform reporting</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✗</span>
                  <span className="text-gray-600">Black-box AI or no AI at all, can't explain decisions to leadership</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✗</span>
                  <span className="text-gray-600">Creative reviews via email threads and Slack chaos</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✗</span>
                  <span className="text-gray-600">Audit trails pieced together manually for compliance</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✗</span>
                  <span className="text-gray-600">No A/B testing structure, no trend detection, reactive optimization</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-green-600 mb-4">With GP Ads AI</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-gray-600">4 hours/week with unified dashboard across all platforms</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-gray-600">Real-time insights with normalized data and custom metrics</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-gray-600">Explainable AI with reasoning for every recommendation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-gray-600">Built-in approval workflows with creative boards and AI copy studio</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-gray-600">Automatic audit trails with action log and alert history</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-gray-600">A/B test manager, trend indicators, conversion funnels, and 25+ analytics tools</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Proven Results for Advocacy Teams
            </h2>
            <p className="text-xl text-gray-600">
              Real impact, measurable outcomes
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <ClockIcon className="h-12 w-12 text-gp-sungold mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">80% Time Savings</h3>
              <p className="text-gray-600 mb-4">
                From 20 hours/week to 4 hours/week per campaign manager
              </p>
              <div className="text-3xl font-bold text-gp-sungold">16 hrs/week</div>
              <p className="text-sm text-gray-500">saved per user</p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <ChartBarIcon className="h-12 w-12 text-gp-forest mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">35+ Features</h3>
              <p className="text-gray-600 mb-4">
                Comprehensive platform replacing 5-10 separate tools
              </p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Campaign Tools</span>
                  <span className="text-sm font-semibold text-gp-forest">6</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Analytics</span>
                  <span className="text-sm font-semibold text-gp-forest">9</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">AI Features</span>
                  <span className="text-sm font-semibold text-gp-forest">6</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Governance</span>
                  <span className="text-sm font-semibold text-gp-forest">6</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <ShieldCheckIcon className="h-12 w-12 text-gp-orange mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Political-Ready</h3>
              <p className="text-gray-600 mb-4">
                Built-in governance and audit trails from day one
              </p>
              <div className="text-3xl font-bold text-gp-orange">100%</div>
              <p className="text-sm text-gray-500">audit readiness</p>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Categories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Comprehensive Platform for Modern Advertising
            </h2>
            <p className="text-xl text-gray-600">
              Purpose-built for advocacy, agencies, and political-adjacent campaigns
            </p>
          </div>

          {/* Campaign Management */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <ChartBarIcon className="h-7 w-7 text-gp-sungold mr-2" />
              Campaign Management & Optimization
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Multi-Platform Dashboard</h4>
                <p className="text-gray-600 text-sm">Unified view of Meta, Google Ads, and TikTok campaigns with real-time sync</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">AI-Powered Campaign Creation</h4>
                <p className="text-gray-600 text-sm">Launch campaigns with intelligent recommendations and pre-filled best practices</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Budget Pacing & Alerts</h4>
                <p className="text-gray-600 text-sm">Automated spend monitoring with proactive notifications for anomalies</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Auto-Pause Rules</h4>
                <p className="text-gray-600 text-sm">Set guardrails to automatically pause underperforming ads and campaigns</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">A/B Test Manager</h4>
                <p className="text-gray-600 text-sm">Design, launch, and analyze experiments with statistical significance tracking</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Optimization Opportunities</h4>
                <p className="text-gray-600 text-sm">AI-identified wins with explainable reasoning and one-click actions</p>
              </div>
            </div>
          </div>

          {/* AI & Creative */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <SparklesIcon className="h-7 w-7 text-gp-sungold mr-2" />
              AI-Powered Creative & Content
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">AI Copy Studio</h4>
                <p className="text-gray-600 text-sm">Generate ad copy variations with performance-informed suggestions</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Creative Library</h4>
                <p className="text-gray-600 text-sm">Centralized asset management with performance tagging and search</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Creative Boards</h4>
                <p className="text-gray-600 text-sm">Kanban-style workflow for organizing and reviewing ad creatives</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Emotional Tone Analytics</h4>
                <p className="text-gray-600 text-sm">Analyze messaging sentiment and emotional resonance in your ads</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">AI Chat Assistant</h4>
                <p className="text-gray-600 text-sm">Conversational AI for instant campaign insights and recommendations</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Copy Performance Tracking</h4>
                <p className="text-gray-600 text-sm">Monitor which messaging drives results with variant-level analytics</p>
              </div>
            </div>
          </div>

          {/* Analytics & Insights */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <ChartBarIcon className="h-7 w-7 text-gp-forest mr-2" />
              Advanced Analytics & Insights
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Analytics Overview</h4>
                <p className="text-gray-600 text-sm">Comprehensive performance dashboard with customizable KPI widgets</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Platform Comparison</h4>
                <p className="text-gray-600 text-sm">Side-by-side performance comparison across Meta, Google, and TikTok</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Spend Dashboard</h4>
                <p className="text-gray-600 text-sm">Budget allocation, pacing, and ROI tracking in real-time</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Demographic Heatmaps</h4>
                <p className="text-gray-600 text-sm">Visualize performance by age, gender, location, and device</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Conversion Funnel</h4>
                <p className="text-gray-600 text-sm">Track user journey from impression to conversion with drop-off analysis</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Trend Indicators</h4>
                <p className="text-gray-600 text-sm">Real-time trend detection with momentum scoring and alerts</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Custom Metrics Builder</h4>
                <p className="text-gray-600 text-sm">Define and track organization-specific KPIs and formulas</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Report Builder</h4>
                <p className="text-gray-600 text-sm">Create custom reports with drag-and-drop widgets and scheduling</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">ML Performance Dashboard</h4>
                <p className="text-gray-600 text-sm">Track AI model accuracy and continuous learning improvements</p>
              </div>
            </div>
          </div>

          {/* Governance & Compliance */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <ShieldCheckIcon className="h-7 w-7 text-gp-orange mr-2" />
              Governance, Compliance & Team Management
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Approval Workflows</h4>
                <p className="text-gray-600 text-sm">Multi-level campaign approval with role-based permissions</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Action Log & Audit Trail</h4>
                <p className="text-gray-600 text-sm">Complete audit history of every change, action, and decision</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Alert History</h4>
                <p className="text-gray-600 text-sm">Centralized view of all system alerts and notifications</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Team Management</h4>
                <p className="text-gray-600 text-sm">User roles, permissions, and access control management</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Ad Account Manager</h4>
                <p className="text-gray-600 text-sm">Connect and manage multiple ad accounts across platforms</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Audience Manager</h4>
                <p className="text-gray-600 text-sm">Create, organize, and sync audiences across platforms</p>
              </div>
            </div>
          </div>

          {/* Integrations & APIs */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <CurrencyDollarIcon className="h-7 w-7 text-gp-forest mr-2" />
              Integrations & Developer Tools
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Platform Integrations</h4>
                <p className="text-gray-600 text-sm">Native connections to Meta, Google Ads, and TikTok advertising APIs</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Slack Integration</h4>
                <p className="text-gray-600 text-sm">Real-time notifications and alerts delivered to your team channels</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">API Keys Management</h4>
                <p className="text-gray-600 text-sm">Secure API access for custom integrations and automation</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Webhooks</h4>
                <p className="text-gray-600 text-sm">Event-driven notifications for external systems and workflows</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Segment Analytics</h4>
                <p className="text-gray-600 text-sm">Track user behavior and events with Segment integration</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">Bulk Actions API</h4>
                <p className="text-gray-600 text-sm">Programmatic campaign management at scale</p>
              </div>
            </div>
          </div>

          {/* Core Platform Features */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <UsersIcon className="h-7 w-7 text-gp-sungold mr-2" />
              Platform Excellence
            </h3>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center p-6">
                <div className="bg-gp-sungold-50 rounded-lg p-3 w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                  <SparklesIcon className="h-6 w-6 text-gp-sungold" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Explainable AI</h4>
                <p className="text-gray-600 text-sm">Every recommendation shows its reasoning</p>
              </div>
              <div className="text-center p-6">
                <div className="bg-gp-forest-50 rounded-lg p-3 w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                  <ClockIcon className="h-6 w-6 text-gp-forest" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Real-Time Sync</h4>
                <p className="text-gray-600 text-sm">Live data updates across all platforms</p>
              </div>
              <div className="text-center p-6">
                <div className="bg-gp-orange-50 rounded-lg p-3 w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                  <ShieldCheckIcon className="h-6 w-6 text-gp-orange" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Enterprise Security</h4>
                <p className="text-gray-600 text-sm">SOC 2 compliant with data isolation</p>
              </div>
              <div className="text-center p-6">
                <div className="bg-gp-sungold-50 rounded-lg p-3 w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                  <UsersIcon className="h-6 w-6 text-gp-sungold" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Agency-Ready</h4>
                <p className="text-gray-600 text-sm">Multi-client support built-in</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Trusted by Advocacy Teams
            </h2>
            <p className="text-xl text-gray-600">
              Join organizations making their advertising accountable
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <p className="text-gray-600 italic mb-4">
                "GP Ads AI's unified dashboard cut our reporting time from 2 days to 2 hours. 
                The real-time platform comparison and custom metrics builder mean I can defend 
                every decision to leadership with confidence."
              </p>
              <div className="flex items-center">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <span className="text-blue-600 font-semibold">A</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Alex Chen</p>
                  <p className="text-sm text-gray-500">Digital Campaign Manager</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <p className="text-gray-600 italic mb-4">
                "The approval workflows and action log are game-changers for political compliance. 
                Auto-pause rules saved us from budget overruns twice last month. The AI chat assistant 
                is like having a data analyst on call 24/7."
              </p>
              <div className="flex items-center">
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                  <span className="text-green-600 font-semibold">J</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Jordan Martinez</p>
                  <p className="text-sm text-gray-500">Sr. Director, Performance Marketing</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <p className="text-gray-600 italic mb-4">
                "The AI Copy Studio and Creative Library transformed our workflow. We test 3x more 
                variations, the emotional tone analytics help us dial in messaging, and the creative 
                boards keep our team organized. ROI improved 40% in 3 months."
              </p>
              <div className="flex items-center">
                <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                  <span className="text-purple-600 font-semibold">S</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Sarah Thompson</p>
                  <p className="text-sm text-gray-500">Head of Growth</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-black border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Make Every Dollar Work Harder?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join advocacy teams who trust GP Ads AI to launch, optimize, and govern their campaigns.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-semibold rounded-md text-black bg-gp-sungold hover:bg-gp-sungold-400 transition-colors"
            >
              Start Free Trial
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
            <button className="inline-flex items-center px-8 py-4 border-2 border-gray-600 text-lg font-medium rounded-md text-white hover:bg-gray-800 transition-colors">
              Request Demo
            </button>
          </div>
          <p className="mt-6 text-sm text-gray-400">
            No credit card required • 14-day free trial • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white">Features</a></li>
                <li><a href="#" className="hover:text-white">Pricing</a></li>
                <li><a href="#" className="hover:text-white">Security</a></li>
                <li><a href="#" className="hover:text-white">Roadmap</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Resources</h3>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white">Documentation</a></li>
                <li><a href="#" className="hover:text-white">API Reference</a></li>
                <li><a href="#" className="hover:text-white">Blog</a></li>
                <li><a href="#" className="hover:text-white">Case Studies</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white">About</a></li>
                <li><a href="#" className="hover:text-white">Careers</a></li>
                <li><a href="#" className="hover:text-white">Contact</a></li>
                <li><a href="#" className="hover:text-white">Partners</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
                <li><a href="#" className="hover:text-white">Compliance</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center">
            <p>&copy; 2026 GP Ads AI. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;
